/**
 * 인앱(임베디드) 웹뷰 감지와 외부 브라우저 URL 생성.
 *
 * 인증 게이트는 isEmbeddedWebView 만 사용한다. 데스크톱 Whale/Chrome과
 * 모바일 Whale처럼 팝업이 되는 일반 브라우저는 절대 막지 않는다.
 */

export const IN_APP_BROWSER_AUTH_CODE = 'auth/in-app-browser';

/** 모바일 앱 인앱 브라우저 전용 마커. 일반 naver/whale/toss 는 넣지 않는다. */
export const INAPP_PATTERNS = [
  { pattern: /KAKAOTALK/i, name: 'kakao' },
  { pattern: /FBAN|FBAV|FB_IAB/i, name: 'facebook' },
  { pattern: /Instagram/i, name: 'instagram' },
  { pattern: /NAVER\(inapp/i, name: 'naver' },
  { pattern: /Line\//i, name: 'line' },
  { pattern: /BytedanceWebview|TikTok/i, name: 'tiktok' },
  { pattern: /DaumApps/i, name: 'daum' },
  { pattern: /Twitter for iPhone|TwitterAndroid/i, name: 'twitter' },
  { pattern: /TossApp/i, name: 'toss' },
  { pattern: /\bBAND\b/, name: 'band' },
];

const fallbackOrigin = 'https://whatquiz.co.kr';

const readUserAgent = (userAgent) => {
  if (typeof userAgent === 'string') return userAgent;
  if (typeof navigator !== 'undefined') return navigator.userAgent || '';
  return '';
};

export const isAndroidUserAgent = (userAgent) => /android/i.test(readUserAgent(userAgent));

export const isIOSUserAgent = (userAgent) => /iphone|ipad|ipod/i.test(readUserAgent(userAgent));

const isMobileUserAgent = (userAgent) => (
  isAndroidUserAgent(userAgent) || isIOSUserAgent(userAgent)
);

const matchKnownInAppApp = (ua) => INAPP_PATTERNS.find(({ pattern }) => pattern.test(ua));

const isAndroidSystemWebView = (ua) => isAndroidUserAgent(ua) && /; wv\)/.test(ua);

const isIosEmbeddedWebView = (ua) => /iPhone|iPad/i.test(ua) && !/Safari\//.test(ua);

export const getInAppBrowserName = (userAgent) => {
  const ua = readUserAgent(userAgent);
  const matched = matchKnownInAppApp(ua);
  if (matched) return matched.name;
  if (isAndroidSystemWebView(ua)) return 'webview';
  if (isIosEmbeddedWebView(ua)) return 'ios_webview';
  return null;
};

/**
 * Google OAuth 팝업이 막히는 모바일 임베디드 웹뷰만 true.
 * 데스크톱과 Whale 은 항상 false.
 */
export const isEmbeddedWebView = (userAgent) => {
  const ua = readUserAgent(userAgent);
  if (!ua || /Whale/i.test(ua)) return false;
  if (!isMobileUserAgent(ua)) return false;
  if (matchKnownInAppApp(ua)) return true;
  if (isAndroidSystemWebView(ua)) return true;
  if (isIosEmbeddedWebView(ua)) return true;
  return false;
};

/** 레거시 별칭. 인증 게이트는 isEmbeddedWebView 를 직접 쓴다. */
export const isInAppBrowser = (userAgent) => isEmbeddedWebView(userAgent);

/** 로그인 없이 링크로 쓰는 화면. 인앱 브라우저에서도 그대로 쓰고, 외부 열기·배너를 띄우지 않는다. */
export const INAPP_EXEMPT_PATH_PREFIXES = ['/private/watkids-eval'];

export const isInAppExemptPath = (pathname = '') => (
  INAPP_EXEMPT_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
);

/**
 * KakaoBrowserDetect 의 자동 외부 열기.
 * 원래 앱 인앱(카카오/인스타 등)에만 해당하고, 일반 `; wv)` 웹뷰는 배너만 띄운다.
 */
export const shouldAutoOpenExternalBrowser = (userAgent) => {
  const ua = readUserAgent(userAgent);
  if (!isEmbeddedWebView(ua)) return false;
  return Boolean(matchKnownInAppApp(ua));
};

const parseUrl = (currentUrl) => {
  const base = typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : fallbackOrigin;
  return new URL(currentUrl || '/', base);
};

export const withCouponQuery = (currentUrl, couponCode) => {
  try {
    const parsed = parseUrl(currentUrl);
    const code = String(couponCode || '').trim();
    if (code && !parsed.searchParams.get('coupon')) {
      parsed.searchParams.set('coupon', code);
    }
    return parsed.toString();
  } catch {
    return currentUrl;
  }
};

export const buildExternalBrowserUrl = (currentUrl, { userAgent } = {}) => {
  const ua = readUserAgent(userAgent);
  let parsed;
  try {
    parsed = parseUrl(currentUrl);
  } catch {
    return currentUrl;
  }

  if (isAndroidUserAgent(ua)) {
    const protocol = (parsed.protocol || 'https:').replace(':', '') || 'https';
    return `intent://${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}#Intent;scheme=${protocol};package=com.android.chrome;end`;
  }

  return parsed.toString();
};

/**
 * 인앱 웹뷰에서 url 을 외부(기본) 브라우저로 연다.
 * - 카카오톡: kakaotalk://web/openExternal (iOS·Android 공통), 500ms 뒤 Android 는 intent 폴백
 * - Android 기타: Chrome intent
 * - iOS 기타(인스타·페북 등): 강제 외부 열기가 불가능하므로 false 를 돌려주고 호출 측이 "링크 복사 + Safari 안내"로 처리한다.
 * @returns {boolean} 외부 열기를 시도했으면 true
 */
export const openExternalBrowser = (url, { userAgent, browserName, navigateTo } = {}) => {
  const ua = readUserAgent(userAgent);
  const name = browserName || getInAppBrowserName(ua);
  const go = typeof navigateTo === 'function'
    ? navigateTo
    : (href) => { window.location.href = href; };
  const android = isAndroidUserAgent(ua);
  try {
    if (name === 'kakao') {
      go(`kakaotalk://web/openExternal?url=${encodeURIComponent(url)}`);
      if (android) {
        setTimeout(() => go(buildExternalBrowserUrl(url, { userAgent: ua })), 500);
      }
      return true;
    }
    if (android) {
      go(buildExternalBrowserUrl(url, { userAgent: ua }));
      return true;
    }
  } catch {
    return false;
  }
  return false;
};

export const createInAppBrowserAuthError = (message = '인앱 브라우저에서는 Google 로그인을 할 수 없어요.') => {
  const error = new Error(message);
  error.code = IN_APP_BROWSER_AUTH_CODE;
  return error;
};
